import React, { useEffect, useMemo, useState } from 'react';
import { attendanceAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { notify } from '../../utils/notify';
import Modal from '../../components/Modal';
import StatusBadge from '../../components/StatusBadge';
import { defaultHours, formFromRecord, hoursFromTimes, payloadFromForm } from './dayForm';
import { payLabel } from './weekModel';
import styles from './TimeAttendance.module.css';

const PREVIEW_DELAY_MS = 400;
const SOURCE_LABEL = {
  employee: 'employee setting',
  settings: 'Settings',
  default: 'default',
};
const money = (v, currency, locale) => `${currency} ${Number(v || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * The side panel to edit one employee's day (Week and Employee tabs). It
 * stores every field create/update keep, shows the rates payroll applies
 * read-only with where they come from, and previews the day's pay with
 * payroll's own calculation (POST /attendance/day-preview).
 */
const DayPanel = ({
  open, onClose, onSaved, employee, day, record, leaveTypes, standardHours, canCreate, canUpdate, canDelete,
}) => {
  const { t, locale } = useLanguage();
  const [form, setForm] = useState(() => formFromRecord(record, employee, standardHours));
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // A new day opens with its own values.
  useEffect(() => {
    setForm(formFromRecord(record, employee, standardHours));
    setConfirmDelete(false);
    setPreview(null);
  }, [record, employee, day, standardHours]);

  const monthly = employee.payment_type !== 'hourly';
  const editable = record ? canUpdate : canCreate;
  const leaveType = leaveTypes.find((lt) => String(lt.id) === String(form.leave_type_id));
  const payload = useMemo(() => payloadFromForm(form, employee, day), [form, employee, day]);
  const payloadKey = JSON.stringify(payload);

  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    const timer = setTimeout(() => {
      setPreviewing(true);
      attendanceAPI.previewDay({ employee_id: employee.id, date: day.date, attendance_id: record ? record.id : undefined, ...JSON.parse(payloadKey) })
        .then((res) => { if (current) setPreview(res.data); })
        .catch(() => { if (current) setPreview(null); })
        .finally(() => { if (current) setPreviewing(false); });
    }, PREVIEW_DELAY_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [open, payloadKey, employee.id, day.date, record]);

  const set = (changes) => setForm((prev) => ({ ...prev, ...changes }));
  const setTimes = (changes) => setForm((prev) => {
    const next = { ...prev, ...changes };
    const hours = hoursFromTimes(next.check_in_time, next.check_out_time, next.break_minutes);
    return hours === null ? next : { ...next, hours_worked: String(hours) };
  });
  const setStatus = (status) => setForm((prev) => {
    const next = { ...prev, status };
    if (status === 'leave' && !next.leave_type_id && leaveTypes.length > 0) next.leave_type_id = String(leaveTypes[0].id);
    const lt = leaveTypes.find((x) => String(x.id) === String(next.leave_type_id));
    const fromTimes = status === 'present' ? hoursFromTimes(next.check_in_time, next.check_out_time, next.break_minutes) : null;
    next.hours_worked = String(fromTimes !== null ? fromTimes : defaultHours(status, lt ? lt.is_paid : false, employee, standardHours));
    return next;
  });

  const save = async () => {
    setBusy(true);
    try {
      if (record) await attendanceAPI.update(record.id, payload);
      else await attendanceAPI.create({ employee_id: employee.id, date: day.date, ...payload });
      notify(t('Day saved'), 'success');
      onSaved();
    } catch (error) {
      notify(error.response?.data?.message || error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await attendanceAPI.delete(record.id);
      notify(t('Day deleted'), 'success');
      onSaved();
    } catch (error) {
      notify(error.response?.data?.message || error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const rate = (multiplier, source) => (source === 'disabled'
    ? t('1.0× · premium disabled in Settings')
    : `${Number(multiplier).toFixed(1)}× · ${t(SOURCE_LABEL[source] || source)}`);
  const dateLabel = new Date(`${day.date}T00:00:00`).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();

  const footer = confirmDelete ? (
    <>
      <span className={styles.grow}>{t('Delete this day? This cannot be undone.')}</span>
      <button type="button" className="btn btn--secondary" onClick={() => setConfirmDelete(false)} disabled={busy}>{t('Cancel')}</button>
      <button type="button" className="btn btn--danger" onClick={remove} disabled={busy}>{t('Delete')}</button>
    </>
  ) : (
    <>
      {record && canDelete && (
        <button type="button" className={styles.btnLinkDanger} onClick={() => setConfirmDelete(true)} disabled={busy}>{t('Delete day')}</button>
      )}
      <span className={styles.grow} />
      <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>{editable ? t('Cancel') : t('Close')}</button>
      {editable && <button type="button" className="btn btn--primary" onClick={save} disabled={busy}>{t('Save')}</button>}
    </>
  );

  return (
    <Modal
      open={open}
      title={fullName}
      subtitle={(
        <>
          <div>{dateLabel}</div>
          <div className={styles.panelPay}>
            <StatusBadge variant="neutral">{monthly ? t('Monthly') : t('Hourly')}</StatusBadge>
            <span>{payLabel(employee, t, locale)}</span>
          </div>
        </>
      )}
      onClose={onClose}
      busy={busy}
      placement="right"
      footer={footer}
    >
      <fieldset className={styles.panelSection} disabled={!editable || busy}>
        <legend className={styles.panelLegend}>{t('Status')}</legend>
        <div className={styles.statusGrid}>
          {[['present', 'Present'], ['absent', 'Absent'], ['sick', 'Sick'], ['leave', 'Leave']].map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={form.status === value}
              className={`${styles.statusBtn} ${form.status === value ? styles[`status_${value}`] : ''}`}
              onClick={() => setStatus(value)}
            >
              {t(label)}
            </button>
          ))}
        </div>
        {form.status === 'leave' && (
          <label className={styles.panelField}>
            <span>{t('Leave type')}</span>
            <select className="select" value={form.leave_type_id} onChange={(e) => setForm((prev) => {
              const lt = leaveTypes.find((x) => String(x.id) === e.target.value);
              return { ...prev, leave_type_id: e.target.value, hours_worked: String(defaultHours('leave', lt ? lt.is_paid : false, employee, standardHours)) };
            })}
            >
              {leaveTypes.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}{lt.is_paid ? '' : ` (${t('unpaid')})`}</option>)}
            </select>
          </label>
        )}

        {form.status === 'present' && (
          <div className={styles.panelRow3}>
            <label className={styles.panelField}>
              <span>{t('Clock in')}</span>
              <input type="time" className="input" value={form.check_in_time} onChange={(e) => setTimes({ check_in_time: e.target.value })} />
            </label>
            <label className={styles.panelField}>
              <span>{t('Clock out')}</span>
              <input type="time" className="input" value={form.check_out_time} onChange={(e) => setTimes({ check_out_time: e.target.value })} />
            </label>
            <label className={styles.panelField}>
              <span>{t('Break (min)')}</span>
              <input type="number" className="input" min="0" step="5" value={form.break_minutes} onChange={(e) => setTimes({ break_minutes: e.target.value })} />
            </label>
          </div>
        )}
        <label className={styles.panelField}>
          <span>{t('Hours counted')}</span>
          <input type="number" className="input" min="0" max="24" step="0.25" value={form.hours_worked} onChange={(e) => set({ hours_worked: e.target.value })} />
        </label>
        <p className={styles.panelHint}>
          {form.status === 'present'
            ? t('From the clock times minus the break; a clock-out earlier than the clock-in ends the next morning.')
            : monthly
              ? t('Monthly employees: a standard day ({hours} h) whatever the status, as payroll counts them today.', { hours: standardHours })
              : t('Hourly employees are paid hours only for days present and paid leave.')}
        </p>
      </fieldset>

      <div className={styles.panelSection}>
        <span className={styles.panelLegend}>{t('Day type, from the calendar')}</span>
        <span>
          {day.is_holiday
            ? t('Public holiday: {name}', { name: day.holiday_name || '' })
            : day.is_sunday ? t('Sunday') : t('Regular day')}
        </span>
        {!day.is_holiday && (
          <label className={styles.panelCheck}>
            <input type="checkbox" checked={form.company_holiday} disabled={!editable || busy} onChange={(e) => set({ company_holiday: e.target.checked })} />
            <span>{t('Company holiday (not in the calendar)')}</span>
          </label>
        )}
      </div>

      {form.status === 'present' && (
        <fieldset className={styles.panelSection} disabled={!editable || busy}>
          <label className={styles.panelCheck}>
            <input type="checkbox" checked={form.has_overtime} onChange={(e) => set({ has_overtime: e.target.checked })} />
            <span className={styles.panelLegend}>{t('Overtime')}</span>
          </label>
          {form.has_overtime && (
            <label className={styles.panelField}>
              <span>{monthly ? t('Overtime (minutes)') : t('Overtime (hours)')}</span>
              <input type="number" className="input" min="0" step={monthly ? '5' : '0.25'} value={form.overtime} onChange={(e) => set({ overtime: e.target.value })} />
            </label>
          )}
          <label className={styles.panelCheck}>
            <input type="checkbox" checked={form.is_late} onChange={(e) => set({ is_late: e.target.checked })} />
            <span className={styles.panelLegend}>{t('Late')}</span>
          </label>
          {form.is_late && (
            <div className={styles.panelRow2}>
              <label className={styles.panelField}>
                <span>{t('Minutes late')}</span>
                <input type="number" className="input" min="0" value={form.late_minutes} onChange={(e) => set({ late_minutes: e.target.value })} />
              </label>
              <label className={styles.panelField}>
                <span>{t('Deduction (% of the day)')}</span>
                <input type="number" className="input" min="0" max="100" value={form.late_deduction_percent} onChange={(e) => set({ late_deduction_percent: e.target.value })} />
              </label>
            </div>
          )}
        </fieldset>
      )}

      {form.status === 'absent' && (
        <fieldset className={styles.panelSection} disabled={!editable || busy}>
          {/* What payroll applies, read-only: the day's own percentage is not used. */}
          <div className={styles.readOnlyRule}>
            <span className={styles.panelLegend}>{t('Absence')}</span>
            <span>{monthly ? t('The whole day is deducted (daily rate)') : t('Not paid (0 h), no deduction on top')}</span>
          </div>
          <label className={styles.panelField}>
            <span>{t('Reason')}</span>
            <input type="text" className="input" value={form.absent_reason} onChange={(e) => set({ absent_reason: e.target.value })} />
          </label>
        </fieldset>
      )}

      {form.status === 'sick' && (
        <fieldset className={styles.panelSection} disabled={!editable || busy}>
          {/* What payroll applies, read-only: the day's own percentage is not used. */}
          <div className={styles.readOnlyRule}>
            <span className={styles.panelLegend}>{t('Sick pay')}</span>
            <span>
              {preview && preview.rates && preview.rates.sick_pay_percentage != null
                ? t('{percent} % · Settings', { percent: Number(preview.rates.sick_pay_percentage) })
                : t('The percentage set in Settings')}
            </span>
          </div>
          <label className={styles.panelField}>
            <span>{t('Reason')}</span>
            <input type="text" className="input" value={form.sick_reason} onChange={(e) => set({ sick_reason: e.target.value })} />
          </label>
        </fieldset>
      )}

      <fieldset className={styles.panelSection} disabled={!editable || busy}>
        <label className={styles.panelField}>
          <span>{t('Notes')}</span>
          <textarea className="textarea" rows="2" value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
        </label>
      </fieldset>

      <section className={styles.panelRates} aria-label={t('Rates payroll applies')}>
        <span className={styles.panelLegend}>{t('Rates payroll applies')}</span>
        {preview && preview.rates ? (
          <dl className={styles.rateList}>
            <dt>{t('Hourly rate')}</dt>
            <dd>{money(preview.rates.hourly_rate, preview.currency, locale)}{monthly ? ` · ${t('from the monthly salary')}` : ''}</dd>
            <dt>{t('Overtime')}</dt>
            <dd>{rate(preview.rates.overtime_multiplier, preview.rates.sources.overtime)}</dd>
            <dt>{t('Sunday')}</dt>
            <dd>{rate(preview.rates.sunday_multiplier, preview.rates.sources.sunday)}</dd>
            <dt>{t('Public holiday')}</dt>
            <dd>{rate(preview.rates.holiday_multiplier, preview.rates.sources.holiday)}</dd>
          </dl>
        ) : <p className={styles.panelHint}>{t('Shown when the day has something to pay.')}</p>}
        <p className={styles.panelHint}>{t('Rates are not edited per day: they come from the employee or Settings.')}</p>
      </section>

      <section className={styles.panelPreview} aria-live="polite" aria-label={t('Pay preview for this day')}>
        <span className={styles.panelLegend}>{t('Pay preview for this day')}{previewing ? ' …' : ''}</span>
        {preview && !preview.empty && preview.lines.map((line) => (
          <div key={`${line.code}-${line.name}`} className={styles.previewLine}>
            <span>{line.name}</span>
            <span className="num">{money(line.amount, preview.currency, locale)}</span>
          </div>
        ))}
        {preview && preview.empty && <p className={styles.panelHint}>{t('Nothing to pay for this day.')}</p>}
        {preview && (
          <div className={`${styles.previewLine} ${styles.previewTotal}`}>
            <span>{t('Total, before taxes')}</span>
            <span className="num">{money(preview.total, preview.currency || employee.currency || 'SRD', locale)}</span>
          </div>
        )}
        <span className={styles.panelHint}>{t('Calculated by payroll itself, as if this day were saved.')}</span>
        {leaveType && form.status === 'leave' && !leaveType.is_paid && <span className={styles.panelHint}>{t('Unpaid leave.')}</span>}
      </section>
    </Modal>
  );
};

export default DayPanel;
